import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ThemeMode, ColorScheme, defaultTheme, defaultColorScheme } from '../../lib/theme.js';
import type { RootState } from '../index.js';

// Define the state type
interface ThemeState {
  mode: ThemeMode;
  colorScheme: ColorScheme;
}

// Initial state
const initialState: ThemeState = {
  mode: defaultTheme,
  colorScheme: defaultColorScheme,
};

// Create the slice
const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<ThemeMode>) => {
      state.mode = action.payload;
      
      // We don't want to manipulate the DOM on the server,
      // and this code will only run on the client after hydration
      if (typeof window !== 'undefined') {
        // Update DOM for theme
        if (action.payload === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
        
        // Store in cookie for server-side renders
        document.cookie = `app-theme-preference=${action.payload}; path=/; max-age=${60 * 60 * 24 * 365}`;
      }
    },
    toggleTheme: (state) => {
      const newTheme = state.mode === 'dark' ? 'light' : 'dark';
      state.mode = newTheme;
      
      // Same client-side only DOM manipulations
      if (typeof window !== 'undefined') {
        if (newTheme === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
        
        document.cookie = `app-theme-preference=${newTheme}; path=/; max-age=${60 * 60 * 24 * 365}`;
      }
    },
    setColorScheme: (state, action: PayloadAction<ColorScheme>) => {
      state.colorScheme = action.payload;
      if (typeof window !== 'undefined') {
        const root = document.documentElement;
        ['theme-blue','theme-green','theme-purple','theme-rose','theme-plain'].forEach(c => root.classList.remove(c));
        root.classList.add(`theme-${action.payload}`);
        document.cookie = `app-color-scheme=${action.payload}; path=/; max-age=${60 * 60 * 24 * 365}`;
      }
    },
    // This action is used by the server to initialize the theme state
    initializeTheme: (state, action: PayloadAction<ThemeMode>) => {
      state.mode = action.payload;
      // No DOM manipulation here
    },
    initializeColorScheme: (state, action: PayloadAction<ColorScheme>) => {
      state.colorScheme = action.payload;
    }
  },
});

// Export actions
export const { setTheme, toggleTheme, setColorScheme, initializeTheme, initializeColorScheme } = themeSlice.actions;

// Export selectors
export const selectTheme = (state: RootState) => state.theme.mode;
export const selectIsDarkMode = (state: RootState) => state.theme.mode === 'dark';
export const selectColorScheme = (state: RootState) => state.theme.colorScheme;

// Export reducer
export default themeSlice.reducer;